import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../cn';
import { buttonCommonClassName, buttonHPadding } from '../common';
import { ButtonPropsSizeType } from '../common.type';
import ButtonIcon from './ButtonIcon';
import ButtonText from './ButtonText';

type ButtonComponent = <ID extends string = string>(props: ButtonProps<ID>) => ReactNode;

type ButtonDot = 'success' | 'error' | 'warning';
type ButtonType = ComponentProps<'button'>['type'] | 'succeed' | 'warn' | 'gradient';

export type ButtonProps<ID extends string = string> = Omit<ComponentProps<'button'>, 'type'> & {
  size?: ButtonPropsSizeType;
  icon?: React.JSX.Element;
  rIcon?: React.JSX.Element;
  text?: boolean;
  active?: boolean;
  id?: ID;
  dot?: ButtonDot;
  textAlign?: 'center' | 'left';
  type?: ButtonType;
  clickable?: boolean;
};

//careful about changing type ='button' as forms with buttons without type act weirdly
const Button: ButtonComponent = ({
  children,
  className,
  disabled,
  type = 'button',
  textAlign = 'center',
  size,
  icon,
  rIcon,
  text,
  active,
  dot,
  clickable,
  ref,
  ...props
}: ButtonProps<string>) => {
  return (
    <button
      className={buttonCommonClassName({
        size,
        disabled,
        type,
        active,
        text,
        className,
        clickable: props.onClick != null || (clickable ?? false) || type == 'submit',
      })}
      type={type === 'succeed' || type === 'warn' || type === 'gradient' ? 'button' : type}
      disabled={disabled}
      ref={ref}
      {...props}>
      {dot && (
        <div
          className={cn(
            {
              'pr-2.5': !icon && children && size == 'xl',
              'pr-2': !icon && children && size == 'lg',
              'pr-1.5': !icon && children && (size == null || size=='base'),
              'pr-1': !icon && children && size == 'sm',
              'pr-0.5': !icon && children && size == 'xs',
            },
            'self-center',
            buttonHPadding(size),
          )}>
          <div
            className={cn('rounded-full', {
              'size-3': size == 'xl',
              'size-2.5': size == 'lg',
              'size-2': size == 'base' || size == null,
              'size-1.5': size == 'sm',
              'size-1': size == 'xs',
              'bg-mantis-500': dot == 'success',
              'bg-fair-pink-500': dot == 'error',
              'bg-quarter-spanish-white-500': dot == 'warning',
            })}
          />
        </div>
      )}
      {icon && <ButtonIcon size={size}>{icon}</ButtonIcon>}
      {children && (
        <ButtonText size={size} className={cn({ 'justify-start text-left': textAlign === 'left' })}>
          {children}
        </ButtonText>
      )}
      {rIcon && <ButtonIcon size={size}>{rIcon}</ButtonIcon>}
    </button>
  );
};

export default Button;
